import { alpha, Box, type MenuProps, styled } from "@mui/material";

import { Radius, Spacing } from "Infrastructure/Themes/Themes";

export const EditorWrapper = styled(Box)<{ $hasError?: boolean }>(
  ({ theme, $hasError }) => ({
    "& .tiptap": {
      backgroundColor: theme.colors.backgroundInput,
      border: `1px solid ${$hasError ? theme.palette.error.main : theme.palette.divider}`,
      borderTop: "none",
      borderBottomLeftRadius: Radius.M,
      borderBottomRightRadius: Radius.M,
      padding: theme.spacing(Spacing.S),
      minHeight: "240px",
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: theme.typography.body2.fontWeight,
      letterSpacing: theme.typography.body2.letterSpacing,
      lineHeight: 1.5,
      outline: "none",
      transition: "border-color 0.2s ease",

      "&:focus": {
        borderColor: $hasError
          ? theme.palette.error.main
          : theme.palette.primary.main,
      },

      "& p": {
        margin: 0,
        marginBottom: theme.spacing(Spacing.S),
      },

      "& p:last-child": {
        marginBottom: 0,
      },

      "& p.is-editor-empty:first-child::before": {
        content: "attr(data-placeholder)",
        color: theme.palette.text.disabled,
        float: "left",
        height: 0,
        pointerEvents: "none",
      },

      "& h1": {
        fontSize: theme.typography.h1.fontSize,
        fontWeight: theme.typography.h1.fontWeight,
        lineHeight: theme.typography.h1.lineHeight,
        margin: 0,
        marginBottom: theme.spacing(Spacing.S),
      },

      "& h2": {
        fontSize: theme.typography.h2.fontSize,
        fontWeight: theme.typography.h2.fontWeight,
        lineHeight: theme.typography.h2.lineHeight,
        letterSpacing: theme.typography.h2.letterSpacing,
        margin: 0,
        marginBottom: theme.spacing(Spacing.S),
      },

      "& h3": {
        fontSize: theme.typography.h3.fontSize,
        fontWeight: theme.typography.h3.fontWeight,
        lineHeight: theme.typography.h3.lineHeight,
        letterSpacing: theme.typography.h3.letterSpacing,
        margin: 0,
        marginBottom: theme.spacing(Spacing.S),
      },

      "& strong": {
        fontWeight: 700,
      },

      "& em": {
        fontStyle: "italic",
      },

      "& u": {
        textDecoration: "underline",
      },

      "& s": {
        textDecoration: "line-through",
      },

      "& mark": {
        backgroundColor: alpha(theme.palette.primary.main, 0.3),
        color: "inherit",
        borderRadius: "2px",
        padding: "0 2px",
      },

      "& code": {
        backgroundColor: alpha(theme.palette.common.white, 0.1),
        borderRadius: "3px",
        padding: "2px 4px",
        fontFamily: "monospace",
        fontSize: "0.9em",
      },

      "& pre": {
        backgroundColor: alpha(theme.palette.common.black, 0.3),
        borderRadius: Radius.M,
        padding: theme.spacing(Spacing.S),
        marginBottom: theme.spacing(Spacing.S),
        overflow: "auto",

        "& code": {
          backgroundColor: "transparent",
          padding: 0,
          borderRadius: 0,
        },
      },

      "& blockquote": {
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        paddingLeft: theme.spacing(Spacing.S),
        marginLeft: 0,
        marginRight: 0,
        marginBottom: theme.spacing(Spacing.S),
        color: theme.palette.text.secondary,
        fontStyle: "italic",
      },

      "& ul, & ol": {
        paddingLeft: theme.spacing(Spacing.M),
        marginBottom: theme.spacing(Spacing.S),
      },

      "& li": {
        marginBottom: theme.spacing(Spacing.XS),
      },

      "& hr": {
        border: "none",
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: `${theme.spacing(Spacing.S)} 0`,
      },

      "& sup": {
        fontSize: "0.75em",
        verticalAlign: "super",
      },

      "& sub": {
        fontSize: "0.75em",
        verticalAlign: "sub",
      },
    },
  }),
);

export const Toolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(Spacing.XS),
  backgroundColor: theme.colors.backgroundTile,
  padding: theme.spacing(Spacing.XS, Spacing.S),
  borderTopLeftRadius: Radius.M,
  borderTopRightRadius: Radius.M,
  flexWrap: "wrap",
  borderBottom: `1px solid ${theme.colors.border}`,
  position: "sticky",
  top: 0,
  zIndex: 10,
}));

export const ToolbarButton = styled("button")<{ $isActive?: boolean }>(
  ({ theme, $isActive }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: $isActive
      ? alpha(theme.palette.primary.main, 0.2)
      : "transparent",
    border: "none",
    borderRadius: Radius.S,
    padding: "4px",
    cursor: "pointer",
    color: $isActive
      ? theme.palette.primary.main
      : alpha(theme.palette.common.white, 0.7),
    fontFamily: theme.typography.fontFamily,
    fontSize: "13px",
    fontWeight: $isActive ? 600 : 400,
    transition: "all 0.15s ease",
    width: "28px",
    height: "28px",

    "& svg": {
      width: "16px",
      height: "16px",
      flexShrink: 0,
    },

    "&:hover": {
      backgroundColor: $isActive
        ? alpha(theme.palette.primary.main, 0.25)
        : alpha(theme.palette.common.white, 0.08),
      color: $isActive
        ? theme.palette.primary.main
        : theme.palette.common.white,
    },

    "&:disabled": {
      opacity: 0.3,
      cursor: "not-allowed",
    },
  }),
);

export const ToolbarDivider = styled(Box)(({ theme }) => ({
  width: "1px",
  height: "20px",
  backgroundColor: alpha(theme.palette.common.white, 0.12),
  margin: "0 2px",
}));

export const MenuToolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(Spacing.XS),
  backgroundColor: theme.colors.backgroundTile,
  padding: theme.spacing(Spacing.XS),
  borderRadius: Radius.L,
  boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.5)}, 0 0 0 1px ${alpha(theme.palette.common.white, 0.08)}`,
}));

export const DropdownTrigger = styled("button")<{ $isActive?: boolean }>(
  ({ theme, $isActive }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: $isActive
      ? alpha(theme.palette.primary.main, 0.2)
      : "transparent",
    border: "none",
    borderRadius: Radius.S,
    padding: "4px 6px",
    cursor: "pointer",
    color: $isActive
      ? theme.palette.primary.main
      : alpha(theme.palette.common.white, 0.7),
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 500,
    transition: "all 0.15s ease",
    height: "28px",
    whiteSpace: "nowrap",

    "& svg": {
      width: "16px",
      height: "16px",
      flexShrink: 0,
    },

    "&:hover": {
      backgroundColor: $isActive
        ? alpha(theme.palette.primary.main, 0.25)
        : alpha(theme.palette.common.white, 0.08),
      color: $isActive
        ? theme.palette.primary.main
        : theme.palette.common.white,
    },
  }),
);

export const dropdownMenuProps: MenuProps["slotProps"] = {
  paper: {
    sx: (theme) => ({
      backgroundColor: theme.colors.backgroundTile,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: Radius.L,
      boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.5)}`,
      minWidth: "160px",
      mt: 0.5,
      "& .MuiMenuItem-root": {
        color: alpha(theme.palette.common.white, 0.7),
        fontSize: "13px",
        padding: "6px 12px",
        gap: "8px",
        "& svg": {
          width: "16px",
          height: "16px",
          flexShrink: 0,
        },
        "&:hover": {
          backgroundColor: alpha(theme.palette.common.white, 0.08),
        },
        "&.Mui-selected": {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          color: theme.palette.primary.main,
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.25),
          },
        },
      },
    }),
  },
};
